import { Fragment } from "react";
import { Check, Minus, ArrowUpRight, Info, MessagesSquare, ShieldCheck, FileSpreadsheet, BrainCircuit, Sparkles } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { BOOKING_PATH } from "@/lib/site";
import { formatPrice } from "@/lib/pricing";
import { PACKAGE_NAMES } from "@/i18n/translations";
import { useLang } from "@/i18n/LanguageContext";
import { trackClick } from "@/lib/track";

// Each column maps (by index) to a key in the central pricing source + the
// canonical package name used for the contact-form pre-select.
const PRICE_KEYS = ["secureSystemAudit", "excelToSoftware", "aiWorkflows"];
const COL_ICONS = [ShieldCheck, FileSpreadsheet, BrainCircuit];

// These are three DIFFERENT service tracks, not a good/better/best ladder, so
// each cell encodes one of three states:
//   INC = included   NO = not included   OPT = optional add-on
// Rows align 1:1 with t.comparison.features order.
const INC = 1;
const NO = 0;
const OPT = 2;
const MATRIX = [
  [INC, INC, NO], //  1  Risk and security audit
  [INC, INC, NO], //  2  Formula and dependency mapping
  [INC, INC, INC], // 3  Implementation roadmap
  [NO, INC, NO], //   4  Documented business rules
  [NO, INC, NO], //   5  Secure software rebuild
  [NO, INC, NO], //   6  Web app, configurator or dashboard
  [NO, INC, NO], //   7  Validation and parity testing
  [NO, INC, INC], //  8  Security hardening (RBAC, MFA, encryption)
  [NO, OPT, INC], //  9  AI workflow design
  [NO, OPT, INC], //  10 Internal AI assistant
  [NO, OPT, INC], //  11 Knowledge base / RAG structure
  [NO, OPT, INC], //  12 Human-in-the-loop controls
  [NO, INC, INC], //  13 Production deployment path
  [OPT, OPT, OPT], // 14 Ongoing maintenance option
];

const selectPackage = (name) => window.dispatchEvent(new CustomEvent("ph:select-package", { detail: name }));

// Three-state table cell. Strong green check, a clearly weaker outline
// "Optional" pill, and a faint dash so the hierarchy reads instantly.
const Cell = ({ state, optLabel }) => {
  if (state === INC)
    return (
      <span className="grid place-items-center h-6 w-6 mx-auto rounded-md bg-[rgba(54,226,160,0.12)] text-[var(--green)]" data-testid="cell-yes">
        <Check size={14} />
      </span>
    );
  if (state === OPT)
    return (
      <span className="inline-flex items-center rounded-full border border-[var(--line-strong)] bg-white/[0.03] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-[var(--muted-2)]" data-testid="cell-optional">
        {optLabel}
      </span>
    );
  return (
    <span className="grid place-items-center h-6 w-6 mx-auto text-[var(--muted-2)] opacity-40" data-testid="cell-no">
      <Minus size={14} />
    </span>
  );
};

// Subtle supporting tags shown under each package name, signalling the depth
// of each track (the 200k rebuild reads as the heaviest technical build).
const Tags = ({ items = [], testId }) => (
  <div className="mt-3 flex flex-wrap gap-1.5" data-testid={testId}>
    {items.map((tg) => (
      <span key={tg} className="rounded-md border border-[var(--line)] bg-white/[0.02] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide text-[var(--blue-bright)]">
        {tg}
      </span>
    ))}
  </div>
);

export const ComparisonSection = () => {
  const { t, lang } = useLang();
  const c = t.comparison;
  const pr = t.pricing;

  // The Secure System Audit (index 0) is the primary booking offer; the others
  // open the contact form with the package pre-selected.
  const colCta = (i, ctaPrefix = "comparison-cta") => {
    if (i === 0) {
      return (
        <a
          href={`${BOOKING_PATH}?src=comparison`}
          onClick={() => trackClick("book_time")}
          className="btn-primary w-full justify-center text-sm"
          data-testid={`${ctaPrefix}-${i}`}
        >
          {c.columns[i].cta} <ArrowUpRight size={15} />
        </a>
      );
    }
    return (
      <a
        href="#kontakt"
        onClick={() => {
          selectPackage(PACKAGE_NAMES[i]);
          trackClick("contact");
        }}
        className={`${i === 1 ? "btn-primary" : "btn-ghost"} w-full justify-center text-sm`}
        data-testid={`${ctaPrefix}-${i}`}
      >
        {c.columns[i].cta} <ArrowUpRight size={15} />
      </a>
    );
  };

  return (
    <section id="priser" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
      <div
        className="glow-orb animate-glow pointer-events-none"
        style={{ zIndex: 0, width: 460, height: 460, top: "0%", right: "6%", background: "var(--blue)", opacity: 0.16 }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={c.eyebrow} title={c.title} intro={c.intro} testId="comparison-heading" />

        {/* Desktop / tablet comparison table */}
        <div className="hidden md:block mt-14 glass-card depth-card rounded-3xl p-2 overflow-hidden" data-testid="comparison-table">
          <div className="grid grid-cols-[1.7fr_1fr_1fr_1fr]">
            {/* Header row */}
            <div className="p-5 flex items-end">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted-2)]">{c.featureCol}</span>
            </div>
            {c.columns.map((col, i) => {
              const Icon = COL_ICONS[i];
              const featured = i === 1;
              return (
                <div
                  key={i}
                  className={`p-5 rounded-t-2xl flex flex-col ${featured ? "bg-[rgba(61,123,255,0.07)] border-x border-t border-[var(--line-strong)]" : ""}`}
                  data-testid={`comparison-col-${i}`}
                >
                  {featured ? (
                    <span className="inline-flex items-center gap-1 self-start rounded-full bg-[var(--blue)] text-white font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 mb-2">
                      <Sparkles size={9} /> {col.tag}
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--blue-bright)]">{col.tag}</span>
                  )}
                  <div className="flex items-start gap-2 mt-2">
                    <span className="grid place-items-center h-9 w-9 shrink-0 rounded-xl border border-[var(--line-strong)] bg-[rgba(61,123,255,0.08)] text-[var(--blue-bright)]">
                      <Icon size={17} />
                    </span>
                    <h3 className="font-display text-base font-semibold text-white leading-snug">{col.name}</h3>
                  </div>
                  {col.desc && <p className="mt-2.5 text-xs text-muted leading-relaxed">{col.desc}</p>}
                  <Tags items={col.tags} testId={`comparison-tags-${i}`} />
                  <div className="mt-auto pt-4 font-sans text-xl font-bold tracking-normal text-[var(--cyan)]" data-testid={`comparison-price-${i}`}>
                    {formatPrice(PRICE_KEYS[i], lang)}
                  </div>
                  {col.roi && <p className="mt-1.5 text-[11px] italic text-[var(--muted-2)] leading-snug" data-testid={`comparison-roi-${i}`}>{col.roi}</p>}
                  {col.creditNote && <p className="mt-1 text-[10px] font-mono uppercase tracking-wide text-[var(--green)]" data-testid={`comparison-credit-${i}`}>{col.creditNote}</p>}
                </div>
              );
            })}

            {/* Best for row */}
            <div className="px-5 py-4 flex items-center bg-white/[0.02]">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--blue-bright)]">{c.bestForLabel}</span>
            </div>
            {c.columns.map((col, i) => (
              <div
                key={i}
                className={`px-5 py-4 ${i === 1 ? "bg-[rgba(61,123,255,0.07)] border-x border-[var(--line-strong)]" : "bg-white/[0.02]"}`}
                data-testid={`comparison-bestfor-${i}`}
              >
                <p className="text-xs text-muted leading-relaxed">{col.bestFor}</p>
              </div>
            ))}

            {/* Feature rows */}
            {c.features.map((f, r) => (
              <Fragment key={r}>
                <div className={`px-5 py-3.5 text-sm text-white/90 ${r % 2 ? "bg-white/[0.015]" : ""}`}>{f}</div>
                {MATRIX[r].map((state, i) => (
                  <div
                    key={i}
                    className={`px-5 py-3.5 flex items-center justify-center ${i === 1 ? "bg-[rgba(61,123,255,0.07)] border-x border-[var(--line-strong)]" : r % 2 ? "bg-white/[0.015]" : ""}`}
                    data-testid={`comparison-cell-${r}-${i}`}
                  >
                    <Cell state={state} optLabel={c.optLabel} />
                  </div>
                ))}
              </Fragment>
            ))}

            {/* CTA row */}
            <div className="px-5 py-5" />
            {c.columns.map((col, i) => (
              <div
                key={i}
                className={`px-4 py-5 rounded-b-2xl ${i === 1 ? "bg-[rgba(61,123,255,0.07)] border-x border-b border-[var(--line-strong)]" : ""}`}
              >
                {colCta(i)}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile stacked cards */}
        <div className="md:hidden mt-12 space-y-5" data-testid="comparison-mobile">
          {c.columns.map((col, i) => {
            const Icon = COL_ICONS[i];
            const featured = i === 1;
            return (
              <div
                key={i}
                className={`rounded-3xl p-6 depth-card ${featured ? "panel border-[var(--line-strong)]" : "glass-card sheen"}`}
                data-testid={`comparison-mcard-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span className="grid place-items-center h-11 w-11 rounded-2xl border border-[var(--line-strong)] bg-[rgba(61,123,255,0.08)] text-[var(--blue-bright)]">
                    <Icon size={20} />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--blue-bright)]">{col.tag}</span>
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-white leading-snug">{col.name}</h3>
                {col.desc && <p className="mt-2 text-xs text-muted leading-relaxed">{col.desc}</p>}
                <div className="mt-3 font-sans text-2xl font-bold tracking-normal text-[var(--cyan)]" data-testid={`comparison-mcard-price-${i}`}>{formatPrice(PRICE_KEYS[i], lang)}</div>
                {col.roi && <p className="mt-1.5 text-[11px] italic text-[var(--muted-2)] leading-snug">{col.roi}</p>}
                {col.creditNote && <p className="mt-1 text-[10px] font-mono uppercase tracking-wide text-[var(--green)]">{col.creditNote}</p>}
                <div className="mt-3 rounded-xl border border-[var(--line)] bg-white/[0.02] px-3 py-2.5" data-testid={`comparison-mcard-bestfor-${i}`}>
                  <span className="block font-mono text-[9px] uppercase tracking-widest text-[var(--blue-bright)] mb-1">{c.bestForLabel}</span>
                  <p className="text-xs text-muted leading-relaxed">{col.bestFor}</p>
                </div>
                <Tags items={col.tags} testId={`comparison-mcard-tags-${i}`} />
                <ul className="mt-4 space-y-2.5">
                  {c.features.map((f, r) => {
                    const state = MATRIX[r][i];
                    if (state === NO) return null;
                    const isOpt = state === OPT;
                    return (
                      <li key={r} className={`flex items-start gap-2.5 text-sm ${isOpt ? "text-muted" : "text-white/90"}`}>
                        {isOpt ? (
                          <span className="shrink-0 mt-0.5 rounded-full border border-[var(--line-strong)] bg-white/[0.03] px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider text-[var(--muted-2)]">
                            {c.optLabel}
                          </span>
                        ) : (
                          <span className="grid place-items-center h-5 w-5 shrink-0 rounded-md bg-[rgba(54,226,160,0.12)] text-[var(--green)] mt-0.5">
                            <Check size={11} />
                          </span>
                        )}
                        {f}
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-5">{colCta(i, "comparison-mcard-cta")}</div>
              </div>
            );
          })}
        </div>

        {/* Notes + dual CTA (shared single source with the central pricing copy) */}
        <div className="mt-6 glass-card depth-card sheen rounded-3xl p-7 lg:p-9" data-testid="comparison-note">
          <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8 items-center">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-9 w-9 shrink-0 rounded-lg bg-[rgba(61,123,255,0.1)] text-[var(--blue-bright)]">
                  <Info size={17} />
                </span>
                <p className="text-sm md:text-base text-white/90 leading-relaxed">{pr.noteMain}</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-9 w-9 shrink-0 rounded-lg bg-[rgba(54,226,160,0.1)] text-[var(--green)]">
                  <MessagesSquare size={17} />
                </span>
                <p className="text-sm md:text-base text-white/90 leading-relaxed">{pr.noteScope}</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="grid place-items-center h-9 w-9 shrink-0 rounded-lg bg-[rgba(61,123,255,0.1)] text-[var(--blue-bright)]">
                  <Info size={17} />
                </span>
                <p className="text-sm text-muted leading-relaxed" data-testid="comparison-tiers-note">{c.tiersNote}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
              <a
                href={`${BOOKING_PATH}?src=comparison`}
                onClick={() => trackClick("book_time")}
                className="btn-primary justify-center"
                data-testid="comparison-cta-primary"
              >
                {pr.cta} <ArrowUpRight size={18} />
              </a>
              <a
                href="#kontakt"
                onClick={() => trackClick("contact")}
                className="btn-ghost justify-center"
                data-testid="comparison-cta-secondary"
              >
                {pr.ctaSecondary} <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
